<template>
  <!-- This file will serve as the hub in which the user will go over a few steps to ensure all relevant transactions (for selected month) are loaded into the monthly overview. -->
  <BModal
    v-model="modal"
    ref="modalRef"
    size="xl"
    ok-title="Save"
    ok-variant="success"
    @ok="handleTrsxnsTransfer"
  >
    <div class="row">
      <div class="col-12">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center"
        >
          <BFormGroup
            id="input-group-filter-start-date"
            label="Start Date:"
            label-for="filter-start-date"
            class="floating-group"
          >
            <BFormInput
              id="filter-start-date"
              v-model="trsxnsStartDate"
              type="date"
              size="sm"
              style="font-size: 0.85rem"
              :class="{ 'is-invalid': trsxnsDateError !== null }"
            />
          </BFormGroup>
          <BFormGroup
            id="input-group-filter-end-date"
            label="End Date:"
            label-for="filter-end-date"
            class="floating-group"
          >
            <BFormInput
              id="filter-end-date"
              v-model="trsxnsEndDate"
              type="date"
              size="sm"
              style="font-size: 0.85rem"
              :class="{ 'is-invalid': trsxnsDateError !== null }"
            />
          </BFormGroup>
          <BFormGroup
            id="input-group-inst-filter"
            label="Institution:"
            label-for="institution-filter"
            class="floating-group"
          >
            <BFormSelect
              id="institution-filter"
              v-model="selectedInstitution"
              :options="institutionOpts"
              size="sm"
              style="font-size: 0.85rem"
            />
          </BFormGroup>
          <span>
            <IconSearch
              size="20"
              class="text-success ms-2"
              style="cursor: pointer"
              @click="handleRefreshClick()"
            />
          </span>
        </div>
      </div>
      <div class="col-sm-12 text-center">
        <span v-if="trsxnsDateError" class="small text-danger">{{
          trsxnsDateError
        }}</span>
      </div>
      <div v-if="transactions" class="col-sm-12">
        <div class="row">
          <div class="col-md-8 col-sm-12 col-12">
            <TrsxnsLoaderListItemView
              v-for="transaction in sortedTransactions"
              :key="transaction.transaction_uid"
              :txn="transaction"
              @transactionSelected="handleTrsxnSelection"
            />
          </div>
          <div class="col-md-4 col-sm-12 col-12">
            <h6 class="mt-2">Step 1: Choose the parent transactions</h6>
            <small
              >These transactions are those that are involved in a payment (most
              likely). They reflect a credit card payment that encompasses
              multiple transactions.</small
            >
            <p v-if="selectedTransactions?.length" class="mb-0 small text-info">
              {{ selectedTransactions.length }} currently selected successfully.
            </p>
            <ul v-if="selectedTransactions?.length">
              <li
                v-for="selected in selectedTransactions"
                :key="selected.transaction_uid"
              >
                {{ selected.description }} -
                {{ formatCurrencyDollarSign(selected.amount) }}
              </li>
            </ul>

            <h6 class="mt-2">Step 2: Fetch the payment transactions</h6>
            <p v-if="step2error" class="mb-0 small text-danger">
              {{ step2error }}
            </p>
            <BButton
              variant="outline-success"
              size="sm"
              :loading="loadingPaymentTrsxns"
              @click="handlePaymentTrsxnsFetch"
            >
              Fetch payment transactions
            </BButton>
            <p v-if="paymentTrsxns?.length" class="mb-0 small text-info">
              {{ paymentTrsxns.length }} payment transactions fetched
              successfully.
            </p>

            <h6 class="mt-2">Step 3: Fetch child transactions</h6>
            <p v-if="step3error" class="mb-0 small text-danger">
              {{ step3error }}
            </p>
            <BButton
              variant="outline-success"
              size="sm"
              :loading="loadingChildTrsxns"
              @click="handleChildTrsxnsFetch"
            >
              Fetch child transactions
            </BButton>
            <p v-if="childTrsxns?.length" class="mb-0 small text-info">
              {{ childTrsxns.length }} child transactions fetched successfully.
            </p>

            <h6 class="mt-2">Step 4: Group and dedup</h6>
            <p v-if="step4error" class="mb-0 small text-danger">
              {{ step4error }}
            </p>
            <BButton
              variant="outline-success"
              size="sm"
              @click="handleGroupAndDedup"
            >
              Group and dedup
            </BButton>
            <p v-if="groupedTrsxns?.length" class="mb-0 small text-info">
              {{ groupedTrsxns.length }} transactions grouped and deduped
              successfully.
            </p>

            <h6 class="mt-2">Step 5: Review and save</h6>
            <p>Click "Save" to append transactions to monthly overview.</p>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>
<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
} from "bootstrap-vue-next";
import { IconSearch } from "@tabler/icons-vue";
import { bankingInstitutions as bankingInstitutionsNs } from "@/store/namespaces";
import TrsxnsLoaderListItemView from "@/components/MonthlyOverview/TrsxnsLoaderListItemView.vue";
import numberFormatMixin from "@/mixins/numberFormatMixin";

export default {
  name: "TrsxnsLoaderModal",
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    IconSearch,
    BFormSelect,
    TrsxnsLoaderListItemView,
    BButton,
  },
  props: {
    acctUid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      modal: false,
      trsxnsStartDate: null,
      trsxnsEndDate: null,
      trsxnsDateError: null,
      loadingTransactions: false,
      transactions: null,
      selectedInstitution: null,

      step2error: null,
      paymentTrsxns: null,
      loadingPaymentTrsxns: false,

      step3error: null,
      childTrsxns: null,
      loadingChildTrsxns: false,

      step4error: null,
      groupedTrsxns: null,
    };
  },
  emits: ["trsxnsLoaded"],
  mixins: [numberFormatMixin],
  computed: {
    bankingInstitutions() {
      return this.$store.getters[
        `${bankingInstitutionsNs}/getBankingInstitutions`
      ];
    },
    institutionOpts() {
      const opts = [{ value: null, text: "— Select —" }];

      if (!this.bankingInstitutions) return opts;

      const institutionOpts = this.bankingInstitutions.map((inst) => {
        const uidLast6 = inst.uid.slice(-6);
        return {
          value: inst,
          text: `${inst.name} - ${inst.acct_last_4} (${uidLast6})`,
        };
      });

      return opts.concat(institutionOpts);
    },
    selectedTransactions() {
      if (!this.transactions) return [];

      return this.transactions.filter((txn) => txn._selected === true);
    },
    filteredTransactions() {
      if (!this.transactions) return [];

      if (!this.selectedInstitution) return this.transactions;

      const filtered = [];
      this.transactions.forEach((txn) => {
        if (txn.institution_uid === this.selectedInstitution.uid) {
          filtered.push(txn);
        }
      });

      return filtered;
    },
    sortedTransactions() {
      const dateSorted = this.filteredTransactions.slice().sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });

      // Next, if a transaction has `snapshot_categories`, we want to place those last, since those are the ones that have been categorized
      return dateSorted.sort((a, b) => {
        if (a.snapshot_categories && !b.snapshot_categories) {
          return 1; // a should come after b
        } else if (!a.snapshot_categories && b.snapshot_categories) {
          return -1; // a should come before b
        } else {
          return 0; // maintain original order
        }
      });
    },
  },
  mounted() {
    // Initialize start date to today's first day of the month and end date to today's last day of the month
    const today = new Date();
    this.trsxnsStartDate = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];
    this.trsxnsEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
  },
  methods: {
    handleTrsxnsTransfer(bvEvt) {
      bvEvt.preventDefault(); // Prevent the modal from closing immediately
      if (!this.groupedTrsxns || this.groupedTrsxns.length === 0) {
        this.$notify.notifyError(
          "No transactions to transfer. Please ensure you have completed the previous steps and have transactions grouped and deduped."
        );
        return;
      }

      this.$emit("trsxnsLoaded", this.groupedTrsxns);
      this.$refs.modalRef.hide();
    },
    handleRefreshClick() {
      this.fetchTransactionsByDateRange();
    },
    handleTrsxnSelection(txn) {
      // Iterate through the transactions and add a new property `_selected` to the one that was selected (remove it if it was already selected)
      this.transactions.forEach((t) => {
        if (t.transaction_uid === txn.transaction_uid) {
          if (Object.keys(t).includes("_selected") && t._selected === true) {
            t._selected = false;
          } else {
            t._selected = true;
          }
        }
      });
    },
    validateTransactionsDateFilters() {
      if (!this.trsxnsStartDate) {
        this.trsxnsDateError = "Start date is required.";
        return false;
      }

      if (!this.trsxnsEndDate) {
        this.trsxnsDateError = "End date is required.";
        return false;
      }

      let [year, month, day] = this.trsxnsStartDate.split("-").map(Number);
      const start = new Date(year, month - 1, day);
      console.log("Start date:", start);
      [year, month, day] = this.trsxnsEndDate.split("-").map(Number);
      const end = new Date(year, month - 1, day);
      console.log("End date:", end);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);

      if (diffDays < 0) {
        this.trsxnsDateError = "End date cannot be before start date.";
        return false;
      }

      // If start date is not the first day of a month, throw error
      if (start.getDate() !== 1) {
        this.trsxnsDateError = "Start date must be the first day of a month.";
        return false;
      }

      // If end date is not the last day of a month, throw error
      const lastDayOfMonth = new Date(
        end.getFullYear(),
        end.getMonth() + 1,
        0
      ).getDate();
      if (end.getDate() !== lastDayOfMonth) {
        this.trsxnsDateError = "End date must be the last day of a month.";
        return false;
      }

      // If the difference between both dates is more than 31 days, throw error
      if (diffDays > 31) {
        this.trsxnsDateError = "Date range cannot be more than 31 days.";
        return false;
      }

      this.trsxnsDateError = null;
      return true;
    },
    async fetchTransactionsByDateRange() {
      if (!this.validateTransactionsDateFilters()) return;

      if (this.acctUid === null) {
        console.warn(
          "No account selected, cannot fetch transactions. Please select an account first."
        );
        return;
      }

      this.transactions = null; // Set to null to show loading state
      this.loadingTransactions = true;
      try {
        const response = await this.$api.get(
          "/banking-institutions/transactions",
          {
            params: {
              filter: [
                `acctUid:eq:${this.acctUid}`,
                `date:gte:${this.trsxnsStartDate}`,
                `date:lte:${this.trsxnsEndDate}`,
              ],
            },
          }
        );
        this.transactions = response.data.map((txn) => txn.item);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        this.loadingTransactions = false;
      }
    },
    checkIfStep2isGoodToGo() {
      if (this.selectedTransactions.length === 0) {
        this.step2error = "Please select at least one transaction.";
        return false;
      }

      if (!this.selectedInstitution) {
        this.step2error = "Please select an institution.";
        return false;
      }

      const allSameInstitution = this.selectedTransactions.every(
        (txn) => txn.institution_uid === this.selectedInstitution.uid
      );
      if (!allSameInstitution) {
        this.step2error =
          "All selected transactions must belong to the selected institution.";
        return false;
      }

      // Ensure transactions aren't duplicated
      const uniqueTransactions = new Set(
        this.selectedTransactions.map((txn) => txn.transaction_uid)
      );
      if (uniqueTransactions.size !== this.selectedTransactions.length) {
        this.step2error = "Duplicate transactions selected.";
        return false;
      }

      this.step2error = null;
      return true;
    },
    async handlePaymentTrsxnsFetch() {
      if (!this.checkIfStep2isGoodToGo()) return;

      this.paymentTrsxns = null;
      this.loadingPaymentTrsxns = true;
      try {
        // We will do a promise.all for each selected transaction to fetch its child transactions
        const response = await Promise.all(
          this.selectedTransactions.map(async (txn) => {
            const params = {
              filter: [
                `acctUid:eq:${this.acctUid}`,
                `parentTrsxnUid:eq:${txn.transaction_uid}`,
              ],
            };

            const res = await this.$api.get(
              `/banking-institutions/transactions`,
              {
                params,
              }
            );
            return res.data.map((txn) => txn.item);
          })
        );
        this.paymentTrsxns = response.flat();
        console.log("Payment transactions response:", this.paymentTrsxns);
        this.$notify.notifySuccess(
          `${this.paymentTrsxns.length} payment transactions fetched successfully.`
        );
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        this.loadingPaymentTrsxns = false;
      }
    },
    async handleChildTrsxnsFetch() {
      if (!this.paymentTrsxns || this.paymentTrsxns.length === 0) {
        this.step3error =
          "No payment transactions to fetch child transactions for.";
        return;
      }

      this.childTrsxns = null;
      this.loadingChildTrsxns = true;
      try {
        // We will do a promise.all for each selected transaction to fetch its child transactions
        const response = await Promise.all(
          this.paymentTrsxns.map(async (txn) => {
            const params = {
              filter: [
                `acctUid:eq:${this.acctUid}`,
                `parentTrsxnUid:eq:${txn.transaction_uid}`,
              ],
            };

            const res = await this.$api.get(
              `/banking-institutions/transactions`,
              {
                params,
              }
            );
            return res.data.map((txn) => txn.item);
          })
        );
        this.childTrsxns = response.flat();
        this.$notify.notifySuccess(
          `${this.childTrsxns.length} child transactions fetched successfully.`
        );
      } catch (error) {
        console.error("Error fetching child transactions:", error);
      } finally {
        this.loadingChildTrsxns = false;
      }
    },
    handleGroupAndDedup() {
      // Group payment transactions with their child transactions and dedup any transactions that may be duplicated between the two lists. We will use the transaction_uid to dedup.
      // if (!this.paymentTrsxns || !this.childTrsxns) {
      //   this.step4error ="Payment transactions or child transactions not loaded, cannot group and dedup.";
      //   return;
      // }

      // Super ultra make sure we are filtering by the correct institution
      console.log("Selected institution:", this.selectedInstitution);
      if (
        !this.selectedInstitution ||
        this.selectedInstitution.name !== "lendingClub"
      ) {
        this.step2error =
          "Please select an institution and ensure it is `lendingClub`.";
        return false;
      }

      const allTrsxnsByUid = {};
      // this.paymentTrsxns.forEach((txn) => {
      //   allTrsxnsByUid[txn.transaction_uid] = txn;
      // });
      // this.childTrsxns.forEach((txn) => {
      //   allTrsxnsByUid[txn.transaction_uid] = txn;
      // });
      this.filteredTransactions.forEach((txn) => {
        allTrsxnsByUid[txn.transaction_uid] = txn;
      });

      this.groupedTrsxns = Object.values(allTrsxnsByUid);
    },
  },
};
</script>
